/**
 * The synthesized "new order" chime.
 *
 * Deliberately not the /sounds/order-is-ready.mp3 voice clip: staff need to
 * tell "an order arrived" from "an order is ready" across a noisy kitchen
 * without looking at the tablet, so the two alerts must not sound alike.
 *
 * Generating it with oscillators also removes an asset from the critical path.
 * A sound file that 404s after a bad deploy fails silently, and an alarm that
 * fails silently is worse than no alarm at all.
 */

// A rising three-note figure — the opposite contour to the spoken clip, and
// pitched high enough to cut through kitchen noise.
const NOTES = [
  { frequency: 880.0, startAt: 0.0, duration: 0.18 },
  { frequency: 1174.66, startAt: 0.16, duration: 0.18 },
  { frequency: 1567.98, startAt: 0.32, duration: 0.42 },
];

const PEAK_GAIN = 0.9;
// Floor for the exponential ramps — exponentialRampToValueAtTime cannot touch
// zero, and a linear ramp clicks audibly at this attack speed.
const SILENT_GAIN = 0.0001;

export function createAlertSound() {
  let context = null;
  let armed = false;
  let liveOscillators = [];

  const getContext = () => {
    if (context) return context;
    const Ctor =
      typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
    if (!Ctor) return null;
    try {
      context = new Ctor();
    } catch {
      return null;
    }
    return context;
  };

  /**
   * Unlock audio. Must be called from inside a user gesture — the app does it
   * on the login tap. Browsers refuse to start an AudioContext otherwise, and
   * the failure is silent, so without this the alarm simply never sounds.
   */
  const arm = async () => {
    const ctx = getContext();
    if (!ctx) return false;
    try {
      await ctx.resume();
    } catch {
      return false;
    }
    armed = ctx.state === 'running';
    return armed;
  };

  const play = async () => {
    const ctx = getContext();
    if (!ctx) return false;

    // The context gets suspended again whenever the tab is backgrounded, so
    // resume on every play rather than trusting the state left by arm().
    if (ctx.state !== 'running') {
      try {
        await ctx.resume();
      } catch {
        return false;
      }
      if (ctx.state !== 'running') return false;
    }
    armed = true;

    const startedAt = ctx.currentTime;
    NOTES.forEach(({ frequency, startAt, duration }) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      const noteStart = startedAt + startAt;

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, noteStart);

      // Percussive envelope: near-instant attack, exponential decay.
      gain.gain.setValueAtTime(SILENT_GAIN, noteStart);
      gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, noteStart + 0.015);
      gain.gain.exponentialRampToValueAtTime(SILENT_GAIN, noteStart + duration);

      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(noteStart);
      oscillator.stop(noteStart + duration + 0.02);

      liveOscillators.push(oscillator);
      oscillator.onended = () => {
        liveOscillators = liveOscillators.filter((node) => node !== oscillator);
      };
    });

    return true;
  };

  /** Cut any chime still sounding. Safe to call when nothing is playing. */
  const stop = () => {
    liveOscillators.forEach((oscillator) => {
      try {
        oscillator.stop();
      } catch {
        // already stopped
      }
    });
    liveOscillators = [];
  };

  return { arm, play, stop, isArmed: () => armed };
}

// The app shares one instance; the factory stays exported for tests.
export const alertSound = createAlertSound();
